import { getTranslation } from './translationUtils';

const t = (key, params) => getTranslation(key, 'badges', undefined, params);

// Функция для получения цвета в зависимости от редкости значка
export const getRarityColor = (rarity) => {
  switch (rarity) {
    case 'common':
      return '#989898';
    case 'uncommon':
      return '#58ac30';
    case 'rare':
      return '#3d85c6';
    case 'epic':
      return '#9b59b6';
    case 'legendary':
      return '#e67e22';
    default:
      return '#989898';
  }
};

// Функция для инициализации критериев по выбранной категории
export const initCriteriaByCategory = (category) => {
  switch (category) {
    case 'course':
      return {
        type: 'courseCompletion',
        courseCount: 1,
      };
    case 'achievement':
      return {
        type: 'achievementCount',
        count: 1,
      };
    case 'community':
      return {
        type: 'forumActivity',
        topicCount: 1,
        postCount: 5,
      };
    case 'streak':
      return {
        type: 'streak',
        minStreak: 3,
      };
    case 'skill':
      return {
        type: 'testCompletion',
        testIds: [],
        minScore: 70,
      };
    case 'seasonal':
      return {
        type: 'seasonalExperience',
        season: 'season1',
        requiredExp: 100,
      };
    case 'challenge':
      return {
        type: 'challengeCompletion',
        challengeId: '',
      };
    default:
      return { type: 'manual' };
  }
};

// Функция для создания составного критерия
export const createCompositeCriteria = (baseCriteria, operator = 'AND') => {
  return {
    operator,
    conditions: [{ ...baseCriteria }],
  };
};

// Функция для валидации критериев перед отправкой
export const validateCriteria = (criteria) => {
  const errors = [];

  // Проверка на отсутствие типа условия
  if (!criteria) {
    errors.push('Критерии не определены');
    return errors;
  }

  // Проверка составных условий
  if (criteria.operator) {
    if (!['AND', 'OR'].includes(criteria.operator)) {
      errors.push('Неверный логический оператор');
    }

    if (
      !criteria.conditions ||
      !Array.isArray(criteria.conditions) ||
      criteria.conditions.length === 0
    ) {
      errors.push('Составные условия должны содержать хотя бы одно условие');
    } else {
      // Рекурсивно проверяем все условия
      criteria.conditions.forEach((condition, index) => {
        const conditionErrors = validateCriteria(condition);
        if (conditionErrors.length > 0) {
          errors.push(
            `Ошибка в условии #${index + 1}: ${conditionErrors.join(', ')}`
          );
        }
      });
    }

    return errors;
  }

  // Проверка наличия типа для простого условия
  if (!criteria.type) {
    errors.push('Тип условия не определен');
    return errors;
  }

  // Проверка по типам
  switch (criteria.type) {
    case 'courseCompletion':
      if (criteria.courseId !== undefined) {
        if (!criteria.courseId) {
          errors.push('Выберите курс');
        }
      } else if (criteria.courseIds !== undefined) {
        if (!criteria.courseIds || criteria.courseIds.length === 0) {
          errors.push('Выберите хотя бы один курс');
        }
      } else if (criteria.courseCount !== undefined) {
        if (criteria.courseCount <= 0) {
          errors.push('Количество курсов должно быть больше 0');
        }
      } else {
        errors.push('Укажите параметры для прохождения курсов');
      }
      break;

    case 'achievementCount':
      if (!criteria.count || criteria.count <= 0) {
        errors.push('Количество достижений должно быть больше 0');
      }
      break;

    case 'specificAchievements':
      if (
        !criteria.achievementIds ||
        !Array.isArray(criteria.achievementIds) ||
        criteria.achievementIds.length === 0
      ) {
        errors.push('Выберите хотя бы одно достижение');
      }
      break;

    case 'testCompletion':
      if (
        !criteria.testIds ||
        !Array.isArray(criteria.testIds) ||
        criteria.testIds.length === 0
      ) {
        errors.push('Выберите хотя бы один тест');
      }
      if (
        !criteria.minScore ||
        criteria.minScore < 0 ||
        criteria.minScore > 100
      ) {
        errors.push('Минимальный балл должен быть между 0 и 100');
      }
      break;

    case 'challengeCompletion':
      if (!criteria.challengeId) {
        errors.push('Выберите испытание');
      }
      break;

    case 'seasonalExperience':
      if (!criteria.season) {
        errors.push('Выберите сезон');
      }
      if (!criteria.requiredExp || criteria.requiredExp <= 0) {
        errors.push('Требуемый опыт должен быть больше 0');
      }
      break;

    case 'forumActivity':
      if (
        (!criteria.topicCount || criteria.topicCount < 0) &&
        (!criteria.postCount || criteria.postCount < 0)
      ) {
        errors.push('Укажите требования к активности на форуме');
      }
      break;

    case 'userLevel':
      if (!criteria.level || criteria.level <= 0) {
        errors.push('Уровень пользователя должен быть больше 0');
      }
      break;

    case 'totalExperience':
      if (!criteria.experience || criteria.experience <= 0) {
        errors.push('Опыт пользователя должен быть больше 0');
      }
      break;

    case 'courseCategoryCompletion':
      if (!criteria.category) {
        errors.push('Выберите категорию курсов');
      }
      if (!criteria.count || criteria.count <= 0) {
        errors.push('Количество курсов в категории должно быть больше 0');
      }
      break;

    default:
      errors.push('Неизвестный тип критерия');
  }

  return errors;
};

// Функция для отображения критериев в виде текста
export const renderCriteriaDescription = (criteria) => {
  if (!criteria) return t('criteriaDescription.notDefined');

  if (criteria.operator) {
    // Составное условие
    const operatorText =
      criteria.operator === 'AND'
        ? t('criteriaDescription.allOf')
        : t('criteriaDescription.anyOf');

    const conditionsText =
      criteria.conditions &&
      criteria.conditions
        .map((condition) => `- ${renderCriteriaDescription(condition)}`)
        .join('\n');

    return `${t(
      'criteriaDescription.mustMeet'
    )} ${operatorText}:\n${conditionsText}`;
  }

  // Простое условие
  switch (criteria.type) {
    case 'courseCompletion':
      if (criteria.courseId) return t('criteriaDescription.specificCourse');
      else if (criteria.courseIds)
        return t('criteriaDescription.specificCourses', {
          count: criteria.courseIds.length,
        });
      else if (criteria.courseCount)
        return t('criteriaDescription.courseCount', {
          count: criteria.courseCount,
        });
      return t('criteriaDescription.courses');

    case 'achievementCount':
      return t('criteriaDescription.achievementCount', {
        count: criteria.count,
      });

    case 'specificAchievements':
      return t('criteriaDescription.specificAchievements', {
        count: criteria.achievementIds?.length || 0,
      });

    case 'forumActivity': {
      let forumText = t('criteriaDescription.forumActivity') + ' ';
      if (criteria.topicCount)
        forumText += t('criteriaDescription.createTopics', {
          count: criteria.topicCount,
        });
      if (criteria.topicCount && criteria.postCount)
        forumText += ' ' + t('criteriaDescription.and') + ' ';
      if (criteria.postCount)
        forumText += t('criteriaDescription.createPosts', {
          count: criteria.postCount,
        });
      return forumText;
    }

    case 'streak':
      return t('criteriaDescription.streak', { count: criteria.minStreak });

    case 'testCompletion': {
      let testText = t('criteriaDescription.pass');
      if (criteria.testIds && criteria.testIds.length) {
        testText += ` ${criteria.testIds.length} `;
        testText +=
          criteria.testIds.length === 1
            ? t('criteriaDescription.testOne')
            : t('criteriaDescription.testsMany');
      } else {
        testText += t('criteriaDescription.tests');
      }
      if (criteria.minScore) {
        testText += t('criteriaDescription.withScore', {
          score: criteria.minScore,
        });
      }
      return testText;
    }

    case 'seasonalExperience':
      return t('criteriaDescription.seasonalExperience', {
        exp: criteria.requiredExp,
        season: criteria.season,
      });

    case 'challengeCompletion':
      return t('criteriaDescription.challengeCompletion');

    case 'userLevel':
      return t('criteriaDescription.userLevel', { level: criteria.level });

    case 'totalExperience':
      return t('criteriaDescription.totalExperience', {
        exp: criteria.experience,
      });

    case 'courseCategoryCompletion':
      return t('criteriaDescription.courseCategoryCompletion', {
        count: criteria.count,
        category: criteria.category,
      });

    case 'manual':
      return t('criteriaDescription.manual');

    default:
      return t('criteriaDescription.unknown', { type: criteria.type });
  }
};
